/**
 * The phone CHANGE EMAIL sheet — `docs/ACCOUNT-IDENTITY.md` step 2.
 *
 * DESIGNED, NOT PORTED. Neither mirror specifies an email-change control:
 * their only email field is IDENTITY's `RECOVERY EMAIL`, saved by SAVE CHANGES
 * with no password gate — while the prototype's own login authenticates against
 * that same value. The design's copy calls it recovery-only; its logic treats it
 * as the login. Marcus settled which is true (2026-08-19): the email IS the
 * login, and changing it needs the current password.
 *
 * That cannot sit behind SAVE CHANGES, so this borrows the shape the column
 * already uses for the other password-gated thing — a 52px row plus a sheet,
 * exactly like CHANGE PASSWORD. Nothing here is invented beyond the copy; the
 * chrome, spacing and type are the password sheet's.
 *
 * ITS OWN FIELDS, NOT `pfCur`. The password sheet clears `pfCur` on both open
 * and close, so sharing it would empty this form the moment either sheet was
 * touched. `emNew` / `emCur` are this sheet's, and `openEm` / `closeEm` clear
 * them the same way.
 *
 * The temp-password population skips the gate for the same reason `savePw`
 * exempts them: the password they hold is the temp one, which `reauth` rejects.
 */

import type { CSSProperties } from 'react';

import { PixelButton } from '../components/PixelButton.tsx';
import { Sheet } from '../components/Sheet.tsx';
import { useQd } from '../store/index.ts';
import { profileVM } from '../store/selectors.ts';

const FIELD: CSSProperties = {
  background: 'var(--gt-field)',
  border: '1px solid var(--gt-border-2)',
  color: 'var(--gt-text)',
  fontFamily: 'var(--gt-font-mono)',
  fontSize: '12px',
  padding: '12px',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const LABEL: CSSProperties = {
  fontFamily: 'var(--gt-font-mono)',
  fontSize: '9px',
  color: 'var(--gt-muted-2)',
};

const GROUP: CSSProperties = { display: 'flex', flexDirection: 'column', gap: '6px' };

export default function EmailSheet(): JSX.Element {
  const s = useQd();
  const v = profileVM(s);

  return (
    <Sheet
      label="Change email sheet"
      maxHeight="90vh"
      onClose={s.closeEm}
      header={{ title: 'CHANGE EMAIL', titleClr: 'var(--gt-text)', titleFs: '8.5px' }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '13px', padding: '15px 14px 18px' }}>
        {/* Says what actually happens, because it is not obvious that this is
            the login rather than a note on file. */}
        <div
          style={{
            fontFamily: 'var(--gt-font-mono)',
            fontSize: '10px',
            lineHeight: 1.7,
            color: 'var(--gt-text-5)',
          }}
        >
          This is the address you sign in with. Changing it changes your login.
        </div>

        <div style={GROUP}>
          <span style={LABEL}>LOGIN EMAIL</span>
          <input
            value={v.emNew}
            onChange={(e) => s.setProfileForm({ emNew: e.target.value })}
            type="email"
            placeholder="YOU@EXAMPLE.COM"
            style={FIELD}
          />
        </div>

        {/* Same rule the password sheet uses: a temp-password account has no
            current password worth asking for. */}
        {v.showCur ? (
          <div style={GROUP}>
            <span style={LABEL}>CURRENT PASSWORD</span>
            <input
              type="password"
              value={v.emCur}
              onChange={(e) => s.setProfileForm({ emCur: e.target.value })}
              style={FIELD}
            />
          </div>
        ) : null}

        <PixelButton
          label={v.emLbl}
          busy={v.emBusy}
          onClick={() => void s.saveEmail()}
          fs={8}
          pad="14px 14px"
          center
        />
      </div>
    </Sheet>
  );
}
